import React, { useEffect, useRef } from 'react';
import { Pin, Archive, ArchiveRestore, Trash2, Edit3, Sparkles, Copy } from 'lucide-react';

/**
 * Floating right-click context menu for Sticky Notes.
 * Positions at the event coordinates and auto-closes on outside click.
 */
const NoteContextMenu = ({ x, y, note, currentUserId, onAction, onClose }) => {
  const ref = useRef(null);

  useEffect(() => {
    const handleClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) onClose();
    };
    const handleEsc = (e) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('mousedown', handleClick);
    window.addEventListener('keydown', handleEsc);
    return () => {
      window.removeEventListener('mousedown', handleClick);
      window.removeEventListener('keydown', handleEsc);
    };
  }, [onClose]);

  if (!note) return null;
  const isOwner = note.created_by === currentUserId;
  const isAssignee = note.assigned_to === currentUserId;
  const canEdit = isOwner || isAssignee;

  // Adjust position to stay inside viewport
  const width = 200;
  const height = 280;
  const adjustedX = Math.min(x, (window.innerWidth || 1200) - width - 10);
  const adjustedY = Math.min(y, (window.innerHeight || 800) - height - 10);

  const Item = ({ icon: Icon, label, action, danger, testId }) => (
    <button
      className={`w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-slate-100 transition-colors ${
        danger ? 'text-red-600 hover:bg-red-50' : 'text-slate-700'
      }`}
      onClick={() => {
        onAction(action, note);
        onClose();
      }}
      data-testid={testId}
    >
      <Icon size={14} />
      {label}
    </button>
  );

  return (
    <div
      ref={ref}
      style={{ left: adjustedX, top: adjustedY }}
      className="fixed z-[100] bg-white rounded-lg shadow-xl border border-slate-200 py-1 min-w-[190px]"
      data-testid="note-context-menu"
    >
      {canEdit && (
        <Item
          icon={Edit3}
          label="Edit"
          action="edit"
          testId="ctx-edit"
        />
      )}
      {canEdit && (
        <Item
          icon={Pin}
          label={note.is_pinned ? 'Unpin' : 'Pin'}
          action="pin"
          testId="ctx-pin"
        />
      )}
      {canEdit && (
        <Item
          icon={Sparkles}
          label="Summarize (AI)"
          action="summarize"
          testId="ctx-summarize"
        />
      )}
      {canEdit && (
        <Item
          icon={Sparkles}
          label="Convert to Task"
          action="convert"
          testId="ctx-convert"
        />
      )}
      <Item
        icon={Copy}
        label="Copy content"
        action="copy"
        testId="ctx-copy"
      />
      <div className="border-t border-slate-100 my-1" />
      {isOwner && !note.is_archived && (
        <Item icon={Archive} label="Archive" action="archive" testId="ctx-archive" />
      )}
      {isOwner && note.is_archived && (
        <Item icon={ArchiveRestore} label="Restore" action="unarchive" testId="ctx-unarchive" />
      )}
      {isOwner && (
        <Item
          icon={Trash2}
          label="Delete"
          action="delete"
          danger
          testId="ctx-delete"
        />
      )}
    </div>
  );
};

export default NoteContextMenu;
